import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { ExploreContext } from "../useExploreContext";
import { toSessionStorageView } from "./useContentView";
import { ExploreDocument, toSessionStorageDoc } from "./useExploreDocument";

// We need just enough information to render a useful tab bar.
type Tab = {
  // type: "question" | "dashboard"; TODO when there is another tab type.
  id: ExploreDocument["id"];
  title: string;
};

export type Tabs = {
  fullscreen: boolean;
  setFullscreen: Dispatch<SetStateAction<boolean>>;
};

export type SerializableTabs = Pick<Tabs, "fullscreen">;

export function useTabs(initial: SerializableTabs | undefined): Tabs {
  const [fullscreen, setFullscreen] = useState(
    initial?.fullscreen === undefined ? true : initial.fullscreen
  );
  return {
    fullscreen,
    setFullscreen,
  };
}

export const serializeTabs = (t?: Partial<SerializableTabs>) =>
  JSON.stringify({
    fullscreen: t?.fullscreen === undefined ? true : Boolean(t?.fullscreen),
  });

export const unserializeTabs: (enc: string) => SerializableTabs = (encoded) => {
  try {
    const data = JSON.parse(encoded);
    if (typeof data === "object" && data) {
      const fullscreen =
        data.fullscreen === undefined ? true : Boolean(data.fullscreen);
      return { fullscreen };
    }
  } catch {}
  return {
    fullscreen: true,
  };
};

type SessionTab = {
  params: string;
  doc: ReturnType<typeof toSessionStorageDoc>;
  view: ReturnType<typeof toSessionStorageView>;
};

// Watches for changes to the current tab, and puts it in session storage
export function useTabsSyncWriter(context: ExploreContext) {
  const id = context.doc.id;
  const params = context.params.serialized;
  const doc = toSessionStorageDoc(context.doc);
  const view = toSessionStorageView(context.view);
  const storable: SessionTab = { params, doc, view };
  const encoded = useDebounce(JSON.stringify(storable), 100);
  useEffect(() => {
    sessionStorage.setItem(`tab#${id}`, encoded);
  }, [id, encoded]);
}

export function findSessionTab(id: string): SessionTab | undefined {
  const encoded = sessionStorage.getItem(`tab#${id}`);
  return encoded ? (JSON.parse(encoded) as SessionTab) : undefined;
}

export function garbageCollectSessionTabs(_tabs: Tab[]): void {
  // Let's just not, and hope that tab bug goes away
  // const keys = Object.keys(sessionStorage);
  // for (const key of keys) {
  //   if (
  //     key.startsWith("tab#") &&
  //     !tabs.some((tab) => key === `tab#${tab.id}`)
  //   ) {
  //     sessionStorage.removeItem(`${key}`);
  //   }
  // }
}
